import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import axios from "axios";
import { 
  ArrowLeft, 
  ArrowRight, 
  Save, 
  Download, 
  FileText, 
  CheckCircle, 
  HelpCircle,
  Clock,
  AlertTriangle,
  Eye
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Questionnaire = () => {
  const { service } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [serviceData, setServiceData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState(null);

  // Fetch service data and questions from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch service details
        const serviceResponse = await axios.get(`${API}/services/${service}`);
        setServiceData(serviceResponse.data);
        
        // Fetch questionnaire questions
        const questionnaireResponse = await axios.get(`${API}/questionnaire/${service}`);
        setQuestions(questionnaireResponse.data.questions || []);
        
        // Load saved progress if exists
        const userId = localStorage.getItem('user_id') || generateUserId();
        localStorage.setItem('user_id', userId);
        
        try {
          const progressResponse = await axios.get(`${API}/questionnaire/progress/${userId}`);
          if (progressResponse.data && progressResponse.data.service_id === parseInt(service)) {
            setAnswers(progressResponse.data.answers || {});
          }
        } catch (progressError) {
          // No saved progress, continue with empty answers
          console.log('No saved progress found');
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching questionnaire data:', err);
        setError('Failed to load questionnaire. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (service) {
      fetchData();
    }
  }, [service]);

  const generateUserId = () => {
    return 'user_' + Math.random().toString(36).substr(2, 9);
  };

  const progress = questions.length > 0 ? ((currentStep + 1) / questions.length) * 100 : 0;
  const currentQuestion = questions[currentStep];

  const saveProgress = async () => {
    try {
      const userId = localStorage.getItem('user_id');
      await axios.post(`${API}/questionnaire/save`, {
        user_id: userId,
        service_id: parseInt(service),
        answers: answers,
        completed: false
      });
      alert("Progress saved! You can return to this questionnaire anytime.");
    } catch (error) {
      console.error('Error saving progress:', error);
      alert("Failed to save progress. Please try again.");
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const nextStep = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setIsComplete(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const generateDocument = async () => {
    try {
      const userId = localStorage.getItem('user_id');
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        alert("Please log in to generate documents.");
        return;
      }
      
      // Prepare authorization headers
      const config = {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      };
      
      // Save final answers
      await axios.post(`${API}/questionnaire/save`, {
        user_id: userId,
        service_id: parseInt(service),
        answers: answers,
        completed: true
      }, config);
      
      // Generate document with authentication
      const response = await axios.post(`${API}/generate-document`, {
        service_id: parseInt(service),
        answers: answers
      }, config);
      
      console.log('Document generation response:', response.data);
      
      if (response.data && response.data.generation_id) {
        alert("Your documents have been generated successfully! You can now download them.");
        
        // Store generation ID for download
        localStorage.setItem('last_generation_id', response.data.generation_id);
        
        setTimeout(() => {
          setShowPreview(true);
        }, 2000);
      } else {
        alert("Document generated but no download link available. Please contact support.");
      }
    } catch (error) {
      console.error('Error generating document:', error);
      
      if (error.response && error.response.status === 401) {
        alert("Authentication failed. Please log in again.");
      } else if (error.response && error.response.status === 403) {
        alert("Access denied. This service may require a subscription upgrade.");
      } else {
        alert("Failed to generate document. Please contact support.");
      }
    }
  };

  const downloadDocument = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      const generationId = localStorage.getItem('last_generation_id');
      
      if (!authToken) {
        alert("Please log in to download documents.");
        return;
      }
      
      if (!generationId) {
        alert("No document available for download. Please generate a document first.");
        return;
      }
      
      // Prepare authorization headers
      const config = {
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        responseType: 'blob' // Important for PDF download
      };
      
      const response = await axios.get(`${API}/download/${generationId}`, config);
      console.log('Download response received');
      
      // Create blob and download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `FormForgePro_Document_${generationId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert("Document downloaded successfully!");
    } catch (error) {
      console.error('Error downloading document:', error);
      
      if (error.response && error.response.status === 401) {
        alert("Authentication failed. Please log in again.");
      } else if (error.response && error.response.status === 403) {
        alert("Access denied. Please check your account permissions.");
      } else if (error.response && error.response.status === 404) {
        alert("Document not found. Please generate a document first.");
      } else {
        alert("Failed to download document. Please contact support.");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          <p className="mt-4 text-gray-600">Loading questionnaire...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Error Loading Questionnaire</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/services')}>
            Back to Services
          </Button>
        </div>
      </div>
    );
  }

  if (!serviceData || !currentQuestion) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Service Not Available</h2>
          <p className="text-gray-600 mb-6">The requested questionnaire could not be found.</p>
          <Button onClick={() => navigate('/services')}>
            Back to Services
          </Button>
        </div>
      </div>
    );
  }

  if (showPreview) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="bg-slate-50 py-8 border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{serviceData.title}</h1>
                <p className="text-gray-600">Document Preview & Download</p>
              </div>
              <Badge className="bg-green-100 text-green-800">
                <CheckCircle className="h-4 w-4 mr-1" />
                Complete
              </Badge>
            </div>
          </div>
        </div>

        {/* Preview Section */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="border-2 border-green-200 mb-8">
            <CardHeader className="bg-green-50">
              <CardTitle className="flex items-center text-green-800">
                <CheckCircle className="h-6 w-6 mr-2" />
                Your Documents Are Ready!
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-gray-700 mb-6">
                Based on your responses, we've generated the following documents for your {serviceData.title}:
              </p>
              
              <div className="space-y-4 mb-6">
                {serviceData.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <FileText className="h-5 w-5 text-blue-500" />
                    <span className="font-medium text-slate-900">{feature}</span>
                    <Badge variant="outline" className="ml-auto">PDF</Badge>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={downloadDocument}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Download All Documents
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white font-semibold px-8 py-3"
                >
                  <Eye className="mr-2 h-5 w-5" />
                  Preview Documents
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Filing Instructions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="h-6 w-6 mr-2 text-blue-500" />
                Filing Instructions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Review Your Documents</h4>
                    <p className="text-gray-600">Carefully review all documents for accuracy before filing.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <h4 className="font-semibold text-slate-900">File with Court</h4>
                    <p className="text-gray-600">Submit your documents to the appropriate court with required fees.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Serve Documents</h4>
                    <p className="text-gray-600">Ensure all parties receive proper legal service as required.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isComplete) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="bg-slate-50 py-8 border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{serviceData.title}</h1>
                <p className="text-gray-600">Questionnaire Complete</p>
              </div>
              <Badge className="bg-orange-100 text-orange-800">
                <CheckCircle className="h-4 w-4 mr-1" />
                Ready to Generate
              </Badge>
            </div>
          </div>
        </div>

        {/* Completion Section */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="text-center">
            <CardContent className="p-12">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Questionnaire Complete!
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                You've successfully answered all questions. We're ready to generate your 
                legal documents based on the information you provided.
              </p>
              
              <div className="bg-slate-50 rounded-lg p-6 mb-8">
                <h3 className="font-semibold text-slate-900 mb-4">Your Documents Will Include:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {serviceData.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  onClick={generateDocument}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-4"
                >
                  <FileText className="mr-2 h-5 w-5" />
                  Generate Documents
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={() => setCurrentStep(0)}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold px-8 py-4"
                >
                  Review Answers
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-slate-50 py-6 border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{serviceData.title}</h1>
              <p className="text-gray-600">Step {currentStep + 1} of {questions.length}</p>
            </div>
            <div className="flex items-center space-x-4">
              <Badge variant="outline" className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                {serviceData.duration}
              </Badge>
              <Button 
                variant="outline"
                size="sm"
                onClick={saveProgress}
                className="flex items-center"
              >
                <Save className="h-4 w-4 mr-1" />
                Save Progress
              </Button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <Progress value={progress} className="h-2" />
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="border-2 border-gray-200">
          <CardHeader>
            <CardTitle className="text-xl flex items-center justify-between">
              <span>{currentQuestion.question}</span>
              {currentQuestion.help && (
                <HelpCircle className="h-5 w-5 text-gray-400" />
              )}
            </CardTitle>
            {currentQuestion.help && (
              <Alert>
                <AlertDescription className="text-sm text-gray-600">
                  {currentQuestion.help}
                </AlertDescription>
              </Alert>
            )}
          </CardHeader>
          
          <CardContent className="p-8">
            {/* Render different input types */}
            {currentQuestion.type === 'text' && (
              <Input
                type="text"
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                placeholder="Enter your answer..."
                className="text-lg p-4 border-2 border-gray-200 focus:border-orange-500"
              />
            )}
            
            {currentQuestion.type === 'textarea' && (
              <Textarea
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                placeholder="Enter your answer..."
                rows={4}
                className="text-lg p-4 border-2 border-gray-200 focus:border-orange-500"
              />
            )}
            
            {currentQuestion.type === 'radio' && (
              <RadioGroup
                value={answers[currentQuestion.id] || ''}
                onValueChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                className="space-y-4"
              >
                {currentQuestion.options?.map((option, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                    <RadioGroupItem value={option} id={`option-${index}`} />
                    <Label htmlFor={`option-${index}`} className="text-lg cursor-pointer">
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}
            
            {currentQuestion.type === 'select' && (
              <select
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                className="w-full text-lg p-4 border-2 border-gray-200 rounded-lg focus:border-orange-500"
              >
                <option value="">Select an option...</option>
                {currentQuestion.options?.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
            className="flex items-center px-6 py-3"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          
          <div className="text-sm text-gray-500">
            Question {currentStep + 1} of {questions.length}
          </div>
          
          <Button
            onClick={nextStep}
            disabled={currentQuestion.required && !answers[currentQuestion.id]}
            className="bg-orange-500 hover:bg-orange-600 text-white flex items-center px-6 py-3"
          >
            {currentStep === questions.length - 1 ? 'Complete' : 'Next'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;